import { Link } from "@tanstack/react-router"
import type { CSSProperties, ReactNode } from "react"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  OperatorAppSidebar,
  type OperatorShellIdentitySummary,
} from "@/features/operator/shared/operator-app-sidebar"
import type {
  OperatorShellNavigationItemState,
  OperatorShellSecondaryNavigationItemState,
} from "@/features/operator/shared/operator-shell-navigation"

export interface OperatorShellBreadcrumb {
  label: string
  to?: string
}

interface OperatorAppShellProps {
  brandHref?: string
  brandName?: string
  brandTagline?: string
  breadcrumbs?: readonly OperatorShellBreadcrumb[]
  children: ReactNode
  footerContent?: ReactNode
  headerActions?: ReactNode
  headerDescription?: string
  headerTitle?: string
  navigationGroupLabel?: string
  navigation: readonly OperatorShellNavigationItemState[]
  secondaryNavigation?: readonly OperatorShellSecondaryNavigationItemState[]
  sidebarDefaultOpen?: boolean
  user: OperatorShellIdentitySummary
}

function OperatorShellBreadcrumbs({
  breadcrumbs,
}: {
  breadcrumbs: readonly OperatorShellBreadcrumb[]
}) {
  if (breadcrumbs.length === 0) {
    return null
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.flatMap((breadcrumb, index) => {
          const key = `${breadcrumb.label}-${index}`

          return [
            <BreadcrumbItem key={key}>
              {breadcrumb.to ? (
                <BreadcrumbLink asChild>
                  <Link to={breadcrumb.to}>{breadcrumb.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>,
            index < breadcrumbs.length - 1 ? (
              <BreadcrumbSeparator key={`${key}-separator`} />
            ) : null,
          ]
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export function OperatorAppShell({
  brandHref,
  brandName,
  brandTagline,
  breadcrumbs = [],
  children,
  footerContent,
  headerActions,
  headerDescription,
  headerTitle,
  navigationGroupLabel,
  navigation,
  secondaryNavigation,
  sidebarDefaultOpen = true,
  user,
}: OperatorAppShellProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <SidebarProvider
        defaultOpen={sidebarDefaultOpen}
        style={
          {
            "--sidebar-width": "18rem",
            "--sidebar-width-mobile": "18rem",
          } as CSSProperties
        }
      >
        <OperatorAppSidebar
          brandHref={brandHref}
          brandName={brandName}
          brandTagline={brandTagline}
          footerContent={footerContent}
          navigationGroupLabel={navigationGroupLabel}
          navigation={navigation}
          secondaryNavigation={secondaryNavigation}
          user={user}
        />
        <SidebarInset className="min-h-svh bg-transparent">
          <header className="sticky top-0 z-20 border-b border-border/60 bg-background/82 backdrop-blur-lg">
            <div className="mx-auto flex min-h-16 w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="h-4" />
              <div className="min-w-0 flex-1">
                <OperatorShellBreadcrumbs breadcrumbs={breadcrumbs} />
                {headerTitle ? (
                  <div className="truncate text-sm font-medium text-foreground">
                    {headerTitle}
                  </div>
                ) : null}
                {headerDescription ? (
                  <p className="line-clamp-1 text-xs text-muted-foreground">
                    {headerDescription}
                  </p>
                ) : null}
              </div>
              {headerActions ? (
                <div className="flex shrink-0 items-center gap-2">
                  {headerActions}
                </div>
              ) : null}
            </div>
          </header>
          <div className="px-4 py-5 sm:px-6 lg:px-8">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
              {children}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
